import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../css/AdminTestPage.css";
import { API_URL } from "../config";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Stack,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ArrowBack } from "@mui/icons-material";

const TestItem = React.memo(({ item, navigateToQuestion, handleEditClick }) => (
  <TestItemContainer variant="outlined">
    <Button variant="contained" sx={{maxWidth:"200px",width:'150px'}}  onClick={() => navigateToQuestion(item.id)}>
      Level: {item.level}
    </Button>
    <Box display="flex" gap={1}>
      <Button
        variant="outlined"
        size="small"
        onClick={() => handleEditClick(item)}
      >
        Edit
      </Button>
      <Button variant="outlined" color="error" size="small">
        Delete
      </Button>
    </Box>
  </TestItemContainer>
));

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
}));
const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));
const TestList = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
}));
const TestItemContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
}));
export default function AdminTestPage() {
  const navigate = useNavigate();
  const { subjectId } = useParams();

  const [tests, setTests] = useState([]);
  const [level, setLevel] = useState("");
  const [year, setYear] = useState(""); // ✅ NEW STATE
  const [subjectName, setSubjectName] = useState(""); // ✅ SUBJECT NAME
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTests, setFilteredTests] = useState([]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editTestId, setEditTestId] = useState(null);

  // 🔥 Fetch Tests + Subject Name
  const fetchTests = useCallback(() => {
    fetch(`${API_URL}/admin/tests/${subjectId}/`)
      .then((response) => response.json())
      .then((data) => {
        setTests(data.tests || data); // in case API wraps response
        setFilteredTests(data.tests || data);
      })
      .catch(() => alert("Failed to fetch tests."));
  }, [subjectId]);

  const fetchSubject = useCallback(() => {
    fetch(`${API_URL}/admin/subject/${subjectId}/`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch subject");
        return res.json();
      })
      .then((data) => {
        setSubjectName(data.name); // adjust if backend uses different key
      })
      .catch((err) => {
        console.error("Failed to fetch subject:", err);
      });
  }, [subjectId]);

  useEffect(() => {
    fetchTests();
    fetchSubject();
  }, [fetchTests, fetchSubject]);

  const resetForm = () => {
    setLevel("");
    setYear("");
    setIsEditMode(false);
    setEditTestId(null);
  };

  const submitTest = () => {
    if (!level || !year) {
      alert("Test level and year are required.");
      return;
    }

    setLoading(true);

    const payload = {
      level,
      year,
    };

    if (isEditMode) {
      fetch(`${API_URL}/admin/test/${editTestId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((updatedTest) => {
          const updatedList = tests.map((test) =>
            test.id === editTestId ? updatedTest : test
          );
          setTests(updatedList);
          setFilteredTests(updatedList);
          resetForm();
        })
        .catch(() => alert("Failed to update test."))
        .finally(() => setLoading(false));
    } else {
      fetch(`${API_URL}/admin/tests/${subjectId}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((newTest) => {
          const newList = [newTest, ...tests];
          setTests(newList);
          setFilteredTests(newList);
          resetForm();
        })
        .catch(() => alert("Failed to add test."))
        .finally(() => setLoading(false));
    }
  };

  const handleEditClick = (test) => {
    setLevel(test.level?.toString());
    setYear(test.year?.toString()); // ✅ LOAD YEAR WHEN EDITING
    setIsEditMode(true);
    setEditTestId(test.id);
  };

  const navigateToQuestion = useCallback(
    (testId) => {
      navigate(`/admin-test-question/${testId}`);
    },
    [navigate]
  );

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      if (query === "") {
        setFilteredTests(tests);
      } else {
        setFilteredTests(
          tests.filter(
            (test) =>
              test.level?.toString().includes(query) ||
              test.year?.toString().includes(query)
          )
        );
      }
    },
    [tests]
  );

  return (
    <PageContainer
      sx={{
        maxWidth: { xs: "100%", sm: 600, md: 900, lg: 1200 },
        px: { xs: 2, sm: 3, lg: 5 },
        py: { xs: 2, sm: 3, lg: 4 },
        mx: "auto",
      }}
    >
      {" "}
      {/* 🔥 Header */}{" "}
      <Header>
        {" "}
        <IconButton onClick={() => navigate(-1)}>
          {" "}
          <ArrowBack />{" "}
        </IconButton>{" "}
        <Typography variant="h5" fontWeight="bold" ml={1}>
          {" "}
          {isEditMode ? "Edit Test Level" : "Manage Test Levels"}{" "}
        </Typography>{" "}
      </Header>{" "}
      {/* 🔥 Subject Name */}{" "}
      {subjectName && (
        <Typography variant="subtitle1" sx={{textTransform:"uppercase",fontWeight:"bold"}} color="text.secondary" mb={3}>
          {" "}
          Subject: {subjectName}{" "}
        </Typography>
      )}{" "}
      {/* 🔥 Form */}{" "}
      <Stack spacing={2} mb={3}>
        {" "}
        <TextField
          label="Test Level"
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          type="number"
          fullWidth
        />{" "}
        <TextField
          label="Year"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          type="number"
          fullWidth
        />{" "}
        <Box display="flex" gap={2}>
          {" "}
          <Button variant="contained" onClick={submitTest} disabled={loading}>
            {" "}
            {loading
              ? isEditMode
                ? "Updating..."
                : "Adding..."
              : isEditMode
              ? "Update Level"
              : "Add Level"}{" "}
          </Button>{" "}
          {isEditMode && (
            <Button variant="outlined" onClick={resetForm}>
              {" "}
              Cancel Edit{" "}
            </Button>
          )}{" "}
        </Box>{" "}
        <TextField
          label="Search by level or year"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          fullWidth
        />{" "}
      </Stack>{" "}
      {/* 🔥 Test List */}{" "}
      <TestList>
        {" "}
        {filteredTests.map((item) => (
          <TestItem
            key={item.id}
            item={item}
            navigateToQuestion={navigateToQuestion}
            handleEditClick={handleEditClick}
          />
        ))}{" "}
      </TestList>{" "}
    </PageContainer>
  );
}
